import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  CalendarRange,
  Car,
  Check,
  ChevronRight,
  Hotel,
  MapPin,
  MessageCircle,
  Package as PackageIcon,
  Repeat2,
  Star,
  TrendingDown,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useSeo from '../hooks/useSeo';
const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
const SITE_URL =
  import.meta.env.VITE_SITE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const formatPrice = (min, max, currency = 'GBP') => {
  if (min == null && max == null) return null;
  const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '';
  const fmt = (n) => `${symbol}${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  if (min != null && max != null && Number(min) === Number(max)) return fmt(min);
  if (min != null && max != null) return `${fmt(min)} - ${fmt(max)}`;
  return fmt(min ?? max);
};

const PriceComparison = ({ pkg, t }) => {
  const ukPrice = formatPrice(pkg.price_uk_min, pkg.price_uk_max, pkg.price_currency || 'GBP');
  const trPrice = formatPrice(pkg.price_tr_min, pkg.price_tr_max, pkg.price_currency || 'GBP');
  if (!ukPrice && !trPrice) return null;

  return (
    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-6">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
          <TrendingDown size={20} />
        </span>
        <h3 className="text-lg font-bold text-[#064e3b]">
          {t('packages.price_comparison', 'UK vs Turkey — Package Price Comparison')}
        </h3>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="rounded-xl bg-white p-4 border border-slate-100">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">
            {t('packages.uk_price', 'UK Price')}
          </p>
          <p className="text-2xl font-bold text-slate-700 line-through opacity-80">
            {ukPrice || '—'}
          </p>
        </div>
        <div className="rounded-xl bg-emerald-600 text-white p-4 shadow-md">
          <p className="text-xs uppercase tracking-wider text-emerald-100 mb-1">
            {t('packages.tr_price', 'Crystal Dental (Turkey)')}
          </p>
          <p className="text-2xl font-bold">{trPrice || '—'}</p>
        </div>
        {pkg.savings_percent ? (
          <div className="rounded-xl bg-white p-4 border border-emerald-200">
            <p className="text-xs uppercase tracking-wider text-emerald-600 mb-1">
              {t('packages.you_save', 'You Save')}
            </p>
            <p className="text-2xl font-bold text-emerald-700">{pkg.savings_percent}%</p>
          </div>
        ) : null}
      </div>
      <p className="mt-3 text-xs text-slate-500">
        {t(
          'packages.price_disclaimer',
          'Package pricing is per patient and indicative; final pricing is confirmed after free consultation and CBCT review.'
        )}
      </p>
    </div>
  );
};

const PackageDetail = () => {
  const { slug } = useParams();
  const { currentLanguage, t } = useLanguage();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSeo(
    'packages',
    currentLanguage,
    pkg
      ? {
          title: pkg.meta_title || `${pkg.title} | Crystal Dental Clinic`,
          description: pkg.meta_description || pkg.short_description || '',
          image: pkg.hero_image_url || pkg.image_url || '',
          canonical: `${SITE_URL}/packages/${pkg.slug}`,
          path: `/packages/${pkg.slug}`,
          hreflangPath: `/packages/${pkg.slug}`,
        }
      : null
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await axios.get(`${API_URL}/api/packages/${slug}`, {
          params: { lang: currentLanguage },
        });
        if (cancelled) return;
        setPkg(res?.data?.data || null);
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 404) setNotFound(true);
          setPkg(null);
        }
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug, currentLanguage]);

  if (loading) {
    return (
      <div className="pt-12 pb-16 container-custom px-4">
        <div className="glass-card p-8 text-[#64748b]">Loading…</div>
      </div>
    );
  }

  if (notFound || !pkg) {
    return (
      <div className="pt-12 pb-16 container-custom px-4">
        <div className="glass-card p-8 text-center">
          <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
            {t('packages.not_found_title', 'Package not found')}
          </h1>
          <p className="text-[#475569] mb-6">
            {t(
              'packages.not_found_body',
              "The package you're looking for doesn't exist or has been moved."
            )}
          </p>
          <Link to="/packages" className="inline-flex items-center gap-2 text-sky-700 font-semibold">
            <ArrowLeft size={16} /> {t('packages.back_to_all', 'Back to all packages')}
          </Link>
        </div>
      </div>
    );
  }

  const paragraphs = (pkg.full_description || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  const includesItems = (pkg.includes_text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const services = Array.isArray(pkg.services) ? pkg.services : [];
  const related = Array.isArray(pkg.related) ? pkg.related : [];
  const heroImage = pkg.hero_image_url || pkg.image_url;

  return (
    <div className="pt-12 pb-16 container-custom px-4 space-y-8">
        <nav className="text-sm text-[#64748b] flex items-center gap-1 flex-wrap">
          <Link to="/" className="hover:text-sky-700">
            Home
          </Link>
          <ChevronRight size={14} />
          <Link to="/packages" className="hover:text-sky-700">
            {t('packages.breadcrumb', 'Treatment Packages')}
          </Link>
          <ChevronRight size={14} />
          <span className="text-[#0f172a] font-medium truncate">{pkg.title || pkg.slug}</span>
        </nav>

        <article className="glass-card overflow-hidden p-0">
          {heroImage ? (
            <div className="relative aspect-[21/9] max-h-[480px] overflow-hidden">
              <img src={heroImage} alt={pkg.title || pkg.slug} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <div className="flex flex-wrap gap-2 mb-3">
                  {pkg.is_featured ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-bold">
                      <Star size={12} /> {t('packages.featured', 'Featured')}
                    </span>
                  ) : null}
                  {pkg.savings_percent ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold">
                      -{pkg.savings_percent}% {t('packages.vs_uk', 'vs UK')}
                    </span>
                  ) : null}
                </div>
                <h1 className="text-2xl md:text-4xl font-extrabold text-white drop-shadow-md">
                  {pkg.title || pkg.slug}
                </h1>
                {pkg.subtitle ? (
                  <p className="text-white/90 text-base md:text-lg mt-2">{pkg.subtitle}</p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="p-8 border-b border-slate-100">
              <h1 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-2">
                {pkg.title || pkg.slug}
              </h1>
              {pkg.subtitle ? (
                <p className="text-sky-700 text-lg font-semibold">{pkg.subtitle}</p>
              ) : null}
            </div>
          )}

          <div className="p-6 md:p-10 space-y-6">
            <ul className="flex flex-wrap gap-2 text-sm">
              {(pkg.duration_days || pkg.duration_nights) ? (
                <li className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 font-semibold">
                  <CalendarRange size={14} />
                  {pkg.duration_days ? `${pkg.duration_days} ${t('packages.days', 'days')}` : ''}
                  {pkg.duration_nights
                    ? ` · ${pkg.duration_nights} ${t('packages.nights', 'nights')}`
                    : ''}
                </li>
              ) : null}
              {pkg.includes_hotel ? (
                <li className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 font-semibold">
                  <Hotel size={14} />
                  {pkg.hotel_stars ? `${pkg.hotel_stars}★ ` : ''}
                  {t('packages.hotel', 'Hotel')}
                  {pkg.hotel_nights ? ` · ${pkg.hotel_nights}n` : ''}
                </li>
              ) : null}
              {pkg.includes_transfer ? (
                <li className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 font-semibold">
                  <Car size={14} /> {t('packages.transfer', 'VIP transfer')}
                </li>
              ) : null}
              {pkg.includes_sightseeing ? (
                <li className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 font-semibold">
                  <MapPin size={14} /> {t('packages.sightseeing', 'Sightseeing tour')}
                </li>
              ) : null}
              {pkg.two_visits ? (
                <li className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 font-semibold">
                  <Repeat2 size={14} /> {t('packages.two_visits', '2 visits required')}
                </li>
              ) : null}
            </ul>

            {paragraphs.length > 0 && (
              <div className="prose-like space-y-4 text-[#475569] leading-8 text-base md:text-lg">
                {paragraphs.map((p, idx) => (
                  <p key={idx} className="whitespace-pre-line">
                    {p}
                  </p>
                ))}
              </div>
            )}

            <PriceComparison pkg={pkg} t={t} />

            {includesItems.length > 0 && (
              <section className="rounded-2xl border border-slate-100 bg-white/60 p-6">
                <h2 className="text-xl font-bold text-[#0f172a] mb-4 flex items-center gap-2">
                  <PackageIcon size={20} className="text-sky-700" />
                  {t('packages.whats_included', "What's included")}
                </h2>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {includesItems.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span className="text-[#0f172a] leading-7">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {services.length > 0 && (
              <section className="rounded-2xl border border-slate-100 bg-white/60 p-6">
                <h2 className="text-xl font-bold text-[#0f172a] mb-4">
                  {t('packages.included_treatments', 'Treatments included in this package')}
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {services.map((s) => (
                    <Link
                      key={s.id}
                      to={`/services/${s.slug}`}
                      className="group block p-4 rounded-xl border border-slate-100 hover:border-sky-300 hover:bg-sky-50/40 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <span className="mt-1 flex-shrink-0 w-7 h-7 rounded-full bg-sky-100 text-sky-700 grid place-items-center group-hover:bg-sky-600 group-hover:text-white transition-colors">
                          <Check size={14} />
                        </span>
                        <div className="flex-1">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <h3 className="font-semibold text-[#0f172a] group-hover:text-sky-700">
                              {s.title || s.slug}
                            </h3>
                            {s.qty > 1 ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 font-bold">
                                ×{s.qty}
                              </span>
                            ) : null}
                          </div>
                          {s.short_description ? (
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                              {s.short_description}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="flex flex-wrap gap-3">
              <Link
                to="/appointment"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-sky-600 to-sky-500 text-white font-semibold shadow-lg hover:shadow-xl transition-shadow"
              >
                <CalendarCheck size={18} />
                {t('packages.cta_book', 'Book Free Consultation')}
              </Link>
              <a
                href="https://wa.me/905495205000"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-emerald-200 text-emerald-700 bg-emerald-50 font-semibold hover:bg-emerald-100"
              >
                <MessageCircle size={18} /> WhatsApp
              </a>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl md:text-2xl font-bold text-[#0f172a]">
                {t('packages.related', 'Other Packages')}
              </h2>
              <Link
                to="/packages"
                className="text-sky-700 font-semibold inline-flex items-center gap-1"
              >
                {t('common.view_all', 'View All')} <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((r) => {
                const trPrice = formatPrice(r.price_tr_min, r.price_tr_max, r.price_currency);
                return (
                  <Link
                    key={r.id}
                    to={`/packages/${r.slug}`}
                    className="block rounded-xl overflow-hidden border border-slate-100 hover:border-sky-300 hover:shadow-md transition-all bg-white"
                  >
                    <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                      {r.image_url ? (
                        <img
                          src={r.image_url}
                          alt={r.title || r.slug}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full grid place-items-center">
                          <PackageIcon className="text-sky-400" size={28} />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-[#0f172a] text-sm leading-snug line-clamp-2">
                        {r.title || r.slug}
                      </h3>
                      {trPrice ? (
                        <p className="text-emerald-700 font-bold text-sm mt-1">{trPrice}</p>
                      ) : null}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
    </div>
  );
};

export default PackageDetail;
